import { useMemo, useState } from "react"
import {
  BedDouble,
  Home,
  Users,
  CheckCircle2,
  Search,
  Copy,
} from "lucide-react"

function StatCard({ icon: Icon, label, value, helper }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-medium text-gray-500">{label}</p>
          <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
          {helper ? (
            <p className="mt-1 text-xs text-gray-500">{helper}</p>
          ) : null}
        </div>
        <div className="rounded-lg bg-gray-50 p-2 text-gray-600">
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </div>
  )
}

/**
 * Props:
 * - units: array from /units
 * - rooms: array from /rooms
 *
 * Expected fields:
 * - units[].unitNumber (room number)
 * - units[].status (Vacant/Occupied) OPTIONAL
 * - rooms[].roomNumber
 */
export default function AvailableUnitsTracker({ units = [], rooms = [] }) {
  const [q, setQ] = useState("")

  // Rooms currently assigned to a unit record
  const assignedRoomNumbers = useMemo(() => {
    return new Set(units.map((u) => u.unitNumber).filter(Boolean))
  }, [units])

  // Available rooms are those not in assignedRoomNumbers
  const availableRooms = useMemo(() => {
    return rooms.filter((r) => !assignedRoomNumbers.has(r.roomNumber))
  }, [rooms, assignedRoomNumbers])

  // If you trust unit.status, you can use it; otherwise occupancy is derived from assignment
  const totalRooms = rooms.length
  const occupiedCount = assignedRoomNumbers.size
  const vacantCount = Math.max(totalRooms - occupiedCount, 0)

  const filteredAvailable = useMemo(() => {
    const query = q.trim().toLowerCase()
    if (!query) return availableRooms

    return availableRooms.filter((r) =>
      String(r.roomNumber).toLowerCase().includes(query)
    )
  }, [availableRooms, q])

  const copyAvailable = async () => {
    const text = filteredAvailable.map((r) => r.roomNumber).join(", ")
    try {
      await navigator.clipboard.writeText(text || "")
    } catch {
      // ignore
    }
  }

  return (
    <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 mb-4">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">
            Available Units Tracker
          </h2>
          <p className="text-sm text-gray-600">
            Quick snapshot of vacant/occupied and available room numbers.
          </p>
        </div>

        <div className="flex items-center gap-2 flex-col md:flex-row">
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search available rooms..."
              className="h-10 w-full md:w-64 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          <button
            type="button"
            onClick={copyAvailable}
            className="inline-flex h-10 items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-100 w-full"
            title="Copy available rooms"
          >
            <Copy className="h-4 w-4" />
            Copy
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard icon={Home} label="Total Rooms" value={totalRooms} />
        <StatCard
          icon={Users}
          label="Occupied"
          value={occupiedCount}
          helper="Assigned to tenants"
        />
        <StatCard
          icon={CheckCircle2}
          label="Vacant"
          value={vacantCount}
          helper="Available rooms"
        />
        <StatCard
          icon={BedDouble}
          label="Available List"
          value={availableRooms.length}
          helper="Unassigned rooms"
        />
      </div>

      <div className="mt-4 rounded-xl border border-gray-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-sm font-semibold text-gray-900">
            Available Rooms ({filteredAvailable.length})
          </p>

          <span className="inline-flex items-center rounded-md bg-green-400/10 px-2 py-1 text-xs font-medium text-green-600">
            Vacant
          </span>
        </div>

        {filteredAvailable.length === 0 ? (
          <p className="text-sm text-gray-500">No available rooms found.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {filteredAvailable.map((r) => (
              <span
                key={r._id || r.roomNumber}
                className="inline-flex items-center rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 text-sm text-gray-700"
              >
                {r.roomNumber}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
